import { AxiosProgressEvent } from "axios";
import { formatAppointmentDateTime } from "../utils/dateUtils";
import api from "./Api";
import { preventDuplicateRequest } from "../utils/requestManager";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

interface AppointmentAction {
  appointmentActionItemName: string;
}

interface ActionOption {
  label: string;
  value: string;
}

const appointment = process.env.REACT_APP_CASE_URL || "";
if (!appointment) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const appointmentAPI = api(appointment);

const getAppointmentSimpleSearch = async (
  appointmentData: any,
): Promise<any> => {
  const requestKey = `getAppointmentSimpleSearch-${JSON.stringify(appointmentData)}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await appointmentAPI.post<ApiResponse<any>>(
        "/api/appointment/summary/drilldown",
        appointmentData,
      );
      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        startTime: formatAppointmentDateTime(data.startTime),
        locations: data.locationCity + "," + data.locationZip,
        subjectqmescheduleid: data.subjectQmeScheduleId,
        reportDueDate: formatAppointmentDateTime(data.dueDate),
      }));
      return updatedResult;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const AppointmentadvanceSearchCaseList = async (params: any): Promise<any> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/advance/search",
      params,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      startTime: formatAppointmentDateTime(data.startTime),
      locations: data.locationCity + "," + data.locationZip,
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const getDoctorNextAvailability = async (
  subjectQMEScheduleIds: any,
): Promise<any> => {
  try {
    const response = await appointmentAPI.get(
      `/api/doctorschedule/check-next-availability?subjectQMEScheduleIds=${subjectQMEScheduleIds}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const RequestForRescheduleAppointment = async (params: any): Promise<any> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/bulk/schedule/update",
      params,
    );

    return response;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};
function getFileNameFromContentDisposition(
  contentDisposition: string | undefined,
): string {
  if (!contentDisposition) return "download.zip";

  // 1️⃣ Check for filename*=UTF-8''
  const utf8FileNameMatch = contentDisposition.match(
    /filename\*\=UTF-8''([^;]+)/i,
  );
  if (utf8FileNameMatch && utf8FileNameMatch[1]) {
    return decodeURIComponent(utf8FileNameMatch[1]);
  }

  // 2️⃣ Fallback: filename=...
  const normalFileNameMatch = contentDisposition.match(/filename\=([^;]+)/i);
  if (normalFileNameMatch && normalFileNameMatch[1]) {
    return normalFileNameMatch[1].replace(/["']/g, "");
  }

  return "download.zip";
}

const downloadQMEFormsZip = async (ids: number[]): Promise<void> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/bulk/generate/qmeform",
      ids, // ✅ raw array, not an object
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      },
    );

    // Extract filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let filename = getFileNameFromContentDisposition(contentDisposition);

    if (contentDisposition) {
      const match = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (match != null) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }
    }

    const blob = new Blob([response.data], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log("Download started:", filename);
  } catch (error) {
    console.error("Error downloading ZIP file:", error);
    throw new Error("Failed to download ZIP file. Please try again.");
  }
};

const downloadQMEFormsZipForRecordList = async (
  ids: number[],
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/bulk/generate/qmeform",
      ids,
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let filename = getFileNameFromContentDisposition(contentDisposition);

    if (contentDisposition) {
      const match = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (match != null) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }
    }

    const blob = new Blob([response.data], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log("Download started:", filename);
  } catch (error) {
    console.error("Error downloading ZIP file:", error);
    throw new Error("Failed to download ZIP file. Please try again.");
  }
};
const downloadSIBTFBulkFormsZipForRecordList = async (
  ids: number[],
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/bulk/generate/sibtfform",
      ids,
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let filename = getFileNameFromContentDisposition(contentDisposition);

    if (contentDisposition) {
      const match = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (match != null) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }
    }

    const blob = new Blob([response.data], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log("Download started:", filename);
  } catch (error) {
    console.error("Error downloading ZIP file:", error);
    throw new Error("Failed to download ZIP file. Please try again.");
  }
};

const downloadSIBTFForm = async (ids: number[]): Promise<void> => {
  try {
    const qmeScheduleId = ids[0]; // assuming one at a time

    const response = await appointmentAPI.post(
      `/api/appointment/generate/sibtfform?qmeScheduleId=${qmeScheduleId}`,
      {}, // request body (empty)
      { responseType: "blob" }, // axios config (CORRECT)
    );

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFileNameFromContentDisposition(contentDisposition);

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log("Download started:", filename);
  } catch (error) {
    console.error("Error downloading SIBTF form:", error);
  }
};

const getRushNotesDetails = async (subjectQmeScheduleID: any): Promise<any> => {
  try {
    const response = await appointmentAPI.get(
      `/api/appointment/rush/notes?subjectQmeScheduleId=${subjectQmeScheduleID}`,
    );

    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const downloadExtensionForm = async (ids: number[]): Promise<void> => {
  try {
    const response = await appointmentAPI.post(
      `/api/appointment/generate/extensionform?dwcExtensionId=${ids}`,
      { ids: ids }, // request body
      { responseType: "blob" }, // axios config (CORRECT)
    );

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFileNameFromContentDisposition(contentDisposition);

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading Extension form:", error);
  }
};

const getAppointmentActionsAccessList = async (
  params: any,
): Promise<ActionOption[]> => {
  try {
    const response = await appointmentAPI.post(
      "/api/appointment/actions/access-list",
      params,
    );

    const result: AppointmentAction[] = response.data?.result || [];

    return result.map((item) => ({
      label: item.appointmentActionItemName,
      value: item.appointmentActionItemName,
    }));
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data.");
  }
};

const getAppointmentSummaryCard = async (data: any): Promise<any> => {
  const requestKey = `getAppointmentSummaryCard-${JSON.stringify(data)}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await appointmentAPI.post<ApiResponse<any>>(
        "/api/appointment/summary",
        data,
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const updateAssignedMyToDo = async (
  data: any,
): Promise<any> => {
  try {
    const response = await appointmentAPI.put<any>(
      "/api/appointment/getAssignMyToDo/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error("Unable to update assigned To. Please try again later.");
  }
};

const GetAssignMyToDoByScheduleId = async (
  qmeScheduleId: any,
): Promise<any> => {
  try {
    const response = await appointmentAPI.get(
      `/api/appointment/getAssignMyToDo/details?qmeScheduleId=${qmeScheduleId}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const GetDoctorUserMappingDetails = async (
  contactUserID: any,
): Promise<any> => {
  try {
    const response = await appointmentAPI.get(
      `/api/appointment/getUserMapping/details/by-DoctorId?contactUserID=${contactUserID}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const updateBlink = async (
  subjectqmescheduleid: number,
  isBlink: number
): Promise<any> => {
  try {
    const response = await appointmentAPI.put(
      `/api/appointment/update/isBlink?subjectqmescheduleid=${subjectqmescheduleid}&isBlink=${isBlink}`
    );

    return response.data;
  } catch (error) {
    throw new Error("Unable to update isBlink. Please try again later.");
  }
};

export {
  getAppointmentSimpleSearch,
  AppointmentadvanceSearchCaseList,
  getDoctorNextAvailability,
  RequestForRescheduleAppointment,
  downloadQMEFormsZip,
  downloadQMEFormsZipForRecordList,
  downloadSIBTFForm,
  getRushNotesDetails,
  downloadExtensionForm,
  getAppointmentActionsAccessList,
  getAppointmentSummaryCard,
  downloadSIBTFBulkFormsZipForRecordList,
  updateAssignedMyToDo,
  GetAssignMyToDoByScheduleId,
  GetDoctorUserMappingDetails,
  updateBlink,
};
